import {
  PROFILE_LOGS_DOMAIN,
  PROFILE_LOGS_PATH
} from 'modules/profile-log/redux/constants'
import getInstancesReduxSelector from 'modules/redux/selectors/get-instances'
import {
  closestIndexTo,
  subDays
} from 'date-fns'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import {
  getCurrentProfileSelect,
  PROFILE_KEY,
  PROFILE_LOG_KEY
} from '@gymaholic/packages'
import isLoadingReduxUtil from 'modules/redux/utils/is-loading'
import isLoadedReduxSelector from 'modules/redux/utils/is-loaded'
import {
  isSameDayDateUtil,
  maxArrayUtil
} from 'packages/src'
import { clampNumberUtil } from 'utils/number/index.js'

export const isLoadingProfileLogsSelector = () => {
  return (state) => {
    return isLoadingReduxUtil(state[PROFILE_LOGS_DOMAIN].all) || state[PROFILE_LOGS_DOMAIN].all.status === null
  }
}

export const isLoadedProfileLogsSelector = () => {
  return (state) => {
    return isLoadedReduxSelector(state[PROFILE_LOGS_DOMAIN].all)
  }
}

const MAXIMUM_WEIGHT = 800
const MINIMUM_WEIGHT = 0

export function getWeightGraphDataSelector () {
  return (state) => {
    const profileLogs = getInstancesReduxSelector(PROFILE_LOGS_PATH.DATA,
      PROFILE_LOGS_PATH.ALL_DATA)(state)

    const currentProfile = getCurrentProfileSelect(state)
    const weightGoal = getValueReduxUtil(currentProfile, PROFILE_KEY.WEIGHT_GOAL)

    // Finding dates of the week to show
    let currentDate = new Date()
    const dates = []
    for (let i = 0; i < 7; i++) {
      if (i === 0) {
        dates.push(currentDate)
        continue
      }
      const newDate = subDays(currentDate, 1)
      currentDate = newDate
      dates.push(newDate)
    }
    dates.reverse()

    const graphData = []

    if (profileLogs.length === 0) {
      return {
        data: [],
        maximum: 1,
        minimum: 0,
        dates,
        weightGoalLineData: [],
        domain: {}
      }
    }

    const sortedProfileLogs = profileLogs.sort((objA, objB) => {
      const objACreatedAt = new Date(getValueReduxUtil(objA, PROFILE_LOG_KEY.CREATED_AT))
      const objBCreatedAt = new Date(getValueReduxUtil(objB, PROFILE_LOG_KEY.CREATED_AT))

      return Number(objACreatedAt) - Number(objBCreatedAt)
    })

    const sortedProfileLogDates = profileLogs.map((profileLog) => {
      const createdAt = new Date(getValueReduxUtil(profileLog, PROFILE_LOG_KEY.CREATED_AT))
      return new Date(createdAt)
    })

    const weightValues = profileLogs.map((profileLog) => {
      const value = getValueReduxUtil(profileLog, PROFILE_LOG_KEY.VALUE)
      return value
    })

    const maximumWeight = clampNumberUtil(maxArrayUtil(weightValues), MINIMUM_WEIGHT, MAXIMUM_WEIGHT)
    const minimumWeight = clampNumberUtil(maxArrayUtil(weightValues), MINIMUM_WEIGHT, MAXIMUM_WEIGHT)

    // Filled the graph data for the dates where profileLog is available
    dates.map((date, index) => {
      const filteredProfileLog = sortedProfileLogs.filter((profileLog) => {
        const createdAt = new Date(getValueReduxUtil(profileLog, PROFILE_LOG_KEY.CREATED_AT))
        return isSameDayDateUtil(createdAt, date)
      })

      let dataPoint
      if (filteredProfileLog.length !== 0) {
        const value = getValueReduxUtil(filteredProfileLog[filteredProfileLog.length - 1], PROFILE_LOG_KEY.VALUE)
        dataPoint = {
          x: date,
          y: value
        }

        const indexOfFilteredProfileLog = sortedProfileLogs.indexOf(filteredProfileLog[0])
        if (sortedProfileLogDates.length > 1) {
          sortedProfileLogDates.splice(indexOfFilteredProfileLog, filteredProfileLog.length)
        }
      }
      graphData[index] = dataPoint

      return null
    })

    // Filling the graphData where profile log is not available
    let lastLoggedWeight
    graphData.map((graphDataPoint, index) => {
      if (!graphDataPoint) {
        // If the last logged weight is avialable
        if (lastLoggedWeight) {
          const dataPoint = {
            x: dates[index],
            y: lastLoggedWeight
          }
          graphData[index] = dataPoint
        } else {
          // Else find the closest date of the week on which the data is available
          const closestToDate = closestIndexTo(dates[index], sortedProfileLogDates)
          const value = getValueReduxUtil(sortedProfileLogs[closestToDate], PROFILE_LOG_KEY.VALUE)
          const dataPoint = {
            x: dates[index],
            y: value
          }
          graphData[index] = dataPoint
        }
      } else {
        lastLoggedWeight = graphDataPoint.y
      }
      return null
    })

    const weightGoalLineData = []
    dates.map((date) => {
      return weightGoalLineData.unshift({
        x: date,
        y: weightGoal
      })
    })

    const minMaxGap = maximumWeight - minimumWeight
    const minMaxGapAddValue = Math.round(minMaxGap / 4)
    const domain = {
      y: [
        // Add `additionalWeightValue` so the line does not get cut down
        // at the bottom of the chart
        // minimumWeight - 10,
        minimumWeight === maximumWeight
          ? 0
          : minimumWeight - minMaxGapAddValue,
        // Add `additionalMaxWeightValue` so the line does not get cut down
        // at the top of the chart
        (
          maximumWeight + minMaxGapAddValue
        )
      ]
    }

    const tickValues = weightGoal > 0
      ? [
          minimumWeight,
          weightGoal,
          maximumWeight
        ]
      : maximumWeight === minimumWeight
        ? [
            0,
            maximumWeight
          ]
        : [
            minimumWeight,
            maximumWeight
          ]

    return {
      data: graphData,
      maximum: maximumWeight,
      minimum: minimumWeight,
      dates: dates,
      weightGoalLineData,
      domain,
      tickValues
    }
  }
}
